import {Box, Tooltip} from "@material-ui/core";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import React, {useMemo, useState} from "react";
import {useTranslate} from "@tolgee/react";
import GlossaryCategoryModal from "view/apps/Organiser/pages/Show/Glossary/GlossaryCategoryModal";
import FilterMenu from "view/components/FilterMenu";
import {UserGlossaryRes} from "hooks/show/useGlossary";
import {ICategory} from "@evennt-tech/api-v2-core";
import {isLinkedCategory} from "api/showInfoLibrary/types";
import allCategory from "./allCategory";
import useStyles from "./styles";

type Props = {
  categories: ICategory[];
  showGlossary: UserGlossaryRes;
  readonly?: boolean;
  includeAllCategory?: boolean;
  selectedCategory?: ICategory | null;
  onSelect: (category: ICategory | null) => void;
  /**
   * Opens the page's `Add from Library` / `Add local` menu instead of the
   * category modal, so a library category can be imported on its own. Falls back
   * to the modal when absent.
   */
  onAddCategory?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

const GlossaryCategories: React.FC<Props> = ({
  onSelect: onSelectCategory,
  readonly,
  showGlossary,
  categories,
  includeAllCategory,
  selectedCategory,
  onAddCategory,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const [isAddCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const createOnClickAdd = () => {
    if (readonly) {
      return undefined;
    }
    if (onAddCategory) {
      return onAddCategory;
    }

    return () => setCategoryModalOpen(true);
  };

  // hide all category only for exhibitors
  const includeAll = !readonly || includeAllCategory;

  const onSelect = (category: ICategory | undefined) => {
    onSelectCategory(category || null);
  };

  /**
   * A book icon beside an inherited category's name, so the rail says which
   * categories the library owns without the organiser having to open each one.
   */
  const railFilters = useMemo(
    () =>
      categories.map((category) =>
        // Organiser-only, like the card badge: an exhibitor reading the manual
        // has no library and no use for where a category came from.
        isLinkedCategory(category) && !readonly
          ? {
              ...category,
              adornment: (
                <Tooltip title="Linked to the library">
                  <MenuBookOutlinedIcon
                    fontSize="inherit"
                    color="primary"
                    style={{verticalAlign: "middle"}}
                  />
                </Tooltip>
              ),
            }
          : category
      ),
    [categories, readonly]
  );

  return (
    <Box className={classes.glossaryCategories}>
      {showGlossary.glossary && (
        <GlossaryCategoryModal
          glossary={showGlossary.glossary}
          categories={showGlossary.categories}
          setSelectedCategory={onSelect}
          isOpen={isAddCategoryModalOpen}
          onClose={() => setCategoryModalOpen(false)}
        />
      )}

      <FilterMenu
        selectedCategory={selectedCategory}
        includeAll={includeAll}
        filters={railFilters}
        allCategory={{...allCategory, title: t("exhibitor_show_info_pg.all")}}
        onSelect={onSelect}
        addLabel="Add Category"
        onClickAdd={createOnClickAdd()}
      />
    </Box>
  );
};

GlossaryCategories.defaultProps = {
  readonly: false,
};

export default GlossaryCategories;
